import { useCallback } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { DashboardsApi } from '@portal/pkg-api/dashboard'
import { data, PowerBiApi } from '@portal/pkg-api/shared'
import { PowerBiEmbedConfig, PowerBiEmbedToken } from '@portal/pkg-api/gen/shared'

const prefix = 'dashboard.dashboards'

/** A config whose embed token has less than this left is fetched again rather than embedded. */
const EMBED_TOKEN_MIN_REMAINING_MILLIS = 2 * 60 * 1000

/** When a config's token stops being good to embed with; NaN when its expiry cannot be read. */
const embedTokenUsableUntil = (config: PowerBiEmbedConfig) =>
  Date.parse(config.embedToken.expiration) - EMBED_TOKEN_MIN_REMAINING_MILLIS

/** Whether a config's token has enough time left to embed with. An unreadable expiry is trusted. */
export const isEmbedTokenUsable = (config: PowerBiEmbedConfig) =>
  !(embedTokenUsableUntil(config) < Date.now())

/** Dashboard endpoints answer 404 for a dashboard the user may not see, not only a missing one. */
export const isNotFound = (error: unknown) => isAxiosError(error) && error.response?.status === 404

export const DashboardQueries = {
  // A 404 is left to the caller, which shows its own page for it, and is not retried: the global
  // policy would retry it indefinitely.
  insightsDashboard: (dashboardId: number) => ({
    queryKey: [`${prefix}.insightsDashboard`, dashboardId],
    queryFn: () =>
      data(
        DashboardsApi.getInsightsDashboard(dashboardId, {
          ignore404: true,
          mercatusNotifyOnError: false,
        }),
      ),
    retry: (failureCount: number, error: unknown) => !isNotFound(error) && failureCount < 3,
  }),
  homepageDashboards: () => ({
    queryKey: [`${prefix}.homepageDashboards`],
    queryFn: () => data(DashboardsApi.getUserHomepageDashboards()),
  }),
  summaryDashboards: (entityId: number) => ({
    queryKey: [`${prefix}.summaryDashboards`, entityId],
    queryFn: () => data(DashboardsApi.getSummaryDashboard(entityId)),
  }),
  // A cached config is only as good as its embed token, so it goes stale once too little of the
  // token is left to embed with. staleTime counts from when the config was stored. The embedded
  // report refreshes its own token, so focus and reconnect refetches are not needed.
  embedConfig: (reportId: string) => ({
    queryKey: [`${prefix}.embedConfig`, reportId],
    queryFn: () => data(PowerBiApi.getEmbedConfig(reportId)),
    enabled: !!reportId,
    staleTime: ({ state }: { state: { data?: PowerBiEmbedConfig; dataUpdatedAt: number } }) => {
      const usableFor = state.data ? embedTokenUsableUntil(state.data) - state.dataUpdatedAt : NaN
      return Number.isNaN(usableFor) ? 0 : Math.max(0, usableFor)
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }),
}

export function useHomepageDashboards() {
  return useQuery(DashboardQueries.homepageDashboards())
}

export function useSummaryDashboard(entityId: number) {
  return useQuery(DashboardQueries.summaryDashboards(entityId))
}

/** Keeps the previous report's config while another report's loads, so the embed stays mounted. */
export function useEmbedConfig(reportId: string) {
  return useQuery({ ...DashboardQueries.embedConfig(reportId), placeholderData: keepPreviousData })
}

/** Stores a refreshed embed token on the cached config, so a later visit can embed with it. */
export function useStoreEmbedToken() {
  const queryClient = useQueryClient()
  return useCallback(
    (reportId: string, embedToken: PowerBiEmbedToken) =>
      queryClient.setQueryData<PowerBiEmbedConfig>(
        DashboardQueries.embedConfig(reportId).queryKey,
        (config) => config && { ...config, embedToken },
      ),
    [queryClient],
  )
}
